/** /regions/ — licensee-scoped Region catalog (list + CRUD + used_by + reorder).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Region has no `updated_ts` — writes are last-writer-wins
 * (no optimistic-concurrency token, unlike tours / trip types).
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type CardArrangement = 'horizontal' | 'vertical';

export interface RegionListItem {
  id: number;
  /** Owning tenant. Null for unassigned/orphaned regions — `Region.tour_operator`
   *  is nullable, so the backend serialises `tenant: null` for those rows. */
  tenant: TenantRef | null;
  name: string;
  custom_id: string | null;
  card_arrangement: CardArrangement;
  card_arrangement_display: string | null;
  countries_count: number;
  has_lat_lng: boolean;
  /** Center point (hidden by default; `has_lat_lng` is the visible marker). */
  latitude: number | null;
  longitude: number | null;
  sequence: number;
}

/** A country option — the picker source and the detail `countries` shape. */
export interface Country {
  id: number;
  name: string;
  country_code: string | null;
}

export interface BoundingBox {
  north: number;
  east: number;
  south: number;
  west: number;
}

export interface RegionDetail extends RegionListItem {
  countries: Country[];
  bounding_box: BoundingBox | null;
  atag: NamedRef[];
  ext_id: string | null;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  card_arrangement?: CardArrangement;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.card_arrangement) usp.set('card_arrangement', params.card_arrangement);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<RegionListItem>> {
  return apiGet<PaginatedResponse<RegionListItem>>(`/regions/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<RegionDetail> {
  return apiGet<RegionDetail>(`/regions/${id}/`);
}

/** Create body — `tour_operator` required; no bounding_box on create. */
export interface RegionCreateInput {
  tour_operator: number;
  name: string;
  custom_id?: string | null;
  card_arrangement?: CardArrangement;
  countries?: number[];
  latitude?: number | null;
  longitude?: number | null;
  atag?: { id: number }[];
}

export function create(body: RegionCreateInput): Promise<RegionDetail> {
  return apiPost<RegionDetail>('/regions/', body);
}

/** PATCH body — any subset. `bounding_box` is a `[north, east, south, west]`
 *  tuple (or null to clear) on write, even though it reads back as an object. */
export interface RegionWriteInput {
  name?: string;
  custom_id?: string | null;
  card_arrangement?: CardArrangement;
  countries?: number[];
  latitude?: number | null;
  longitude?: number | null;
  bounding_box?: [number, number, number, number] | null;
  atag?: { id: number }[];
}

export function update(id: number, body: RegionWriteInput): Promise<RegionDetail> {
  return apiPatch<RegionDetail>(`/regions/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/regions/${id}/`);
}

export function reorder(tourOperator: number, orderedIds: number[]): Promise<{ status: string }> {
  return apiPost('/regions/reorder/', { tour_operator: tourOperator, ordered_ids: orderedIds });
}

/** Country picker source — `GET /countries/?q=`. */
export function listCountries(q?: string): Promise<{ results: Country[] }> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return apiGet(`/countries/${qs}`);
}
